/**
 * The tick loop, and the split between deterministic simulation and rendering.
 *
 * `fixedUpdate` advances only on a committed command set supplied by a relay,
 * never on elapsed time. `update` runs at display rate and exposes `alpha` for
 * interpolation. A client that has not received the set for the tick it needs
 * **stalls** — stalling is correct and visible; guessing is a desync.
 */

import { Registry } from './registry.js'
import { HashRegistry } from './hash.js'
import { Rng } from './rng.js'
import { TICK_HZ, TICK_MS, HASH_INTERVAL } from './config.js'

/**
 * The engine's view of a relay. Kept structural and generic so `src/core/` does
 * not depend on `src/net/` — the concrete `Relay` in `src/net/relay.ts`
 * satisfies this shape.
 */
export interface CommittedSource<TCommand> {
  /** The committed set for `tick`, or `null` if it has not arrived yet. */
  commandsFor(tick: number): readonly TCommand[] | null
  /** Called after `tick` has executed, so the relay can release its ring slot. */
  onTickExecuted?(tick: number, hash: number | null): void
}

/** What a tick applier does with a committed set. Supplied by `src/sim/`. */
export type CommandApplier<TCommand> = (tick: number, commands: readonly TCommand[]) => void

export interface EngineOptions<TCommand> {
  registry: Registry
  source: CommittedSource<TCommand>
  applyCommands: CommandApplier<TCommand>
  hash: HashRegistry
  rng: Rng
  /** Ticks between hash checkpoints. Defaults to config's HASH_INTERVAL. */
  hashInterval?: number
}

export class DoubleTickError extends Error {
  constructor(tick: number) {
    super(`tick ${tick} executed twice`)
    this.name = 'DoubleTickError'
  }
}

export class Engine<TCommand> {
  readonly registry: Registry
  readonly hash: HashRegistry
  readonly rng: Rng

  private readonly source: CommittedSource<TCommand>
  private readonly applyCommands: CommandApplier<TCommand>
  private readonly hashInterval: number

  /** Index of the next tick to execute. Ticks are executed 0, 1, 2, ... */
  private nextTick = 0
  private lastExecuted = -1
  private accumulatorMs = 0
  private stalled = false

  /** Checkpoint hashes keyed by tick, kept for the desync report and replays. */
  readonly hashTrace: Array<{ tick: number; hash: number }> = []

  constructor(opts: EngineOptions<TCommand>) {
    this.registry = opts.registry
    this.source = opts.source
    this.applyCommands = opts.applyCommands
    this.hash = opts.hash
    this.rng = opts.rng
    this.hashInterval = opts.hashInterval ?? HASH_INTERVAL
  }

  get tick(): number {
    return this.nextTick
  }

  /** True when the last `step()` could not advance for want of a command set. */
  get isStalled(): boolean {
    return this.stalled
  }

  /**
   * Advance exactly one tick if its command set is committed.
   * Returns false — without side effects — when it is not.
   */
  step(): boolean {
    const tick = this.nextTick
    const commands = this.source.commandsFor(tick)
    if (commands === null) {
      this.stalled = true
      return false
    }
    if (tick <= this.lastExecuted) throw new DoubleTickError(tick)

    this.stalled = false
    this.registry.time.tick = tick
    this.applyCommands(tick, commands)
    this.registry.fixedUpdate(tick)

    this.lastExecuted = tick
    this.nextTick = tick + 1

    let checkpoint: number | null = null
    if (this.hashInterval > 0 && this.nextTick % this.hashInterval === 0) {
      checkpoint = this.hash.compute()
      this.hashTrace.push({ tick: this.nextTick, hash: checkpoint })
    }
    this.source.onTickExecuted?.(tick, checkpoint)
    return true
  }

  /**
   * Advance until `target` (exclusive) or until a stall.
   * Returns the number of ticks actually executed.
   */
  runTo(target: number): number {
    let n = 0
    while (this.nextTick < target) {
      if (!this.step()) break
      n++
    }
    return n
  }

  /**
   * Drive from wall-clock time. Browser-side entry point.
   *
   * `maxCatchUp` bounds how many ticks one frame may execute so a long stall
   * does not turn into a multi-second freeze once the sets arrive.
   */
  /**
   * Advance by wall-clock time.
   *
   * `opts.render` skips the render-rate pass while still taking the fixed
   * steps. A hidden tab must keep stepping — its lockstep peer is waiting on
   * its input — but drawing to a tab nobody is looking at is pure waste, and on
   * a throttled background thread it is waste that costs the simulation its
   * budget.
   */
  frame(dtMs: number, opts: { maxCatchUp?: number; render?: boolean } = {}): void {
    const maxCatchUp = opts.maxCatchUp ?? 8
    this.accumulatorMs += Math.min(dtMs, TICK_MS * maxCatchUp * 2)
    let executed = 0
    while (this.accumulatorMs >= TICK_MS && executed < maxCatchUp) {
      if (!this.step()) break
      this.accumulatorMs -= TICK_MS
      executed++
    }
    if (opts.render === false) return
    // Never interpolate past the tick we have actually executed.
    const alpha = Math.min(this.accumulatorMs / TICK_MS, 1)
    this.registry.update(dtMs / 1000, alpha)
  }

  /** Current full-state hash, outside the checkpoint schedule. */
  currentHash(): number {
    return this.hash.compute()
  }

  /** Reset the wall-clock accumulator, e.g. after a tab regains focus. */
  resetClock(): void {
    this.accumulatorMs = 0
  }
}

export { TICK_HZ, TICK_MS }
